import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import {
  IndicatorSectionKey,
  IndicatorWithUpdate,
} from '../ImpactIndicator/utils';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';
import { faLock, faTrash } from '@fortawesome/pro-light-svg-icons';
import StagesModal from '../ImpactIndicator/Stages/StagesModal';
import IndicatorTextarea from '../ImpactIndicator/IndicatorTextarea';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import RestoreIndicator from '../ImpactIndicator/Stages/RestoreIndicator';
import { getCardTitle } from '../ImpactIndicator/IndicatorCardDetails';

interface SectionRightProps {
  selectedIndicators: IndicatorWithUpdate[];
  reportId: number;
  beneficiaryTerm: string | undefined;
  sectionKey: IndicatorSectionKey;
  programName?: string;
  intermediaries?: string;
  isReportPassed?: boolean;
  serviceGoal?: string;
  serviceType?: string;
  unselectedIndicators: IndicatorWithUpdate[];
  deletedIndicators: IndicatorWithUpdate[];
  handleRestoreIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  handleDelete: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  handleAdd: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  handleIndicatorTextUpdate: (
    sectionKey: IndicatorSectionKey,
    text: string,
    index: number,
  ) => unknown;
}

interface DeleteIndicatorProps {
  lockDelete?: boolean;
  className?: string;
  handleClick: () => unknown;
}

const DeleteIndicator: React.FC<DeleteIndicatorProps> = ({
  lockDelete,
  className,
  handleClick,
}) => {
  if (lockDelete) {
    return (
      <TrueImpactIconButton
        color="blackBase"
        label="Cannot remove this indicator"
        className={className}
        icon={faLock}
      />
    );
  }
  return (
    <TrueImpactIconButton
      color="blackBase"
      label="Delete"
      icon={faTrash}
      className={className}
      onClick={handleClick}
    />
  );
};

const StyledDiv = styled.div`
  flex: 1;
  padding-right: 14px;
  max-width: 738px;
  .indicator {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .indicator-name {
      width: 200px;
      word-break: break-word;
      margin-right: ${SPACERS.small};
    }
    .indicator-textarea {
      flex: 1;
    }
    .indicator-delete {
      margin-right: ${SPACERS.small};
      margin-left: ${SPACERS.small};
    }
  }
  .indicator-border {
    margin-bottom: ${SPACERS.medium};
    padding-bottom: ${SPACERS.medium};
    border-bottom: 1px solid ${COLORS.grayLight};
  }
  .deleted-indicators {
    margin-bottom: ${SPACERS.medium};
  }
`;

const SectionRight: React.FC<SectionRightProps> = ({
  reportId,
  selectedIndicators,
  deletedIndicators,
  beneficiaryTerm,
  handleRestoreIndicator,
  handleDelete,
  handleAdd,
  handleIndicatorTextUpdate,
  sectionKey,
  intermediaries,
  isReportPassed = false,
  programName,
  serviceGoal,
  serviceType,
  unselectedIndicators,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onAdd = useCallback(
    (v: IndicatorWithUpdate, i: number) => {
      handleAdd(sectionKey, v, i);
      handleClose();
    },
    [handleAdd, handleClose, sectionKey],
  );
  return (
    <StyledDiv>
      {deletedIndicators.length > 0 && (
        <div className="deleted-indicators">
          {deletedIndicators.map((d, i) => (
            <RestoreIndicator
              index={i}
              key={d.id}
              indicator={d}
              sectionKey={sectionKey}
              handleRestoreIndicator={handleRestoreIndicator}
            />
          ))}
        </div>
      )}
      {selectedIndicators.map((indicator, index) => {
        const title = getCardTitle(
          indicator,
          beneficiaryTerm,
          isReportPassed,
          sectionKey,
          programName,
          intermediaries,
          undefined,
          serviceGoal,
          serviceType,
        );
        return (
          <div
            key={index}
            className={`indicator${
              index !== selectedIndicators.length - 1 ? ' indicator-border' : ''
            }`}
          >
            <TrueImpactText variant="body-bold" className="indicator-name">
              {title}
            </TrueImpactText>
            <IndicatorTextarea
              sectionKey={sectionKey}
              index={index}
              indicator={indicator}
              reportId={reportId}
              programName={programName}
              beneficiaryTerm={beneficiaryTerm}
              className="indicator-textarea"
              textareaBackgroundColor="grayLight"
              handleIndicatorTextUpdate={handleIndicatorTextUpdate}
              intermediaries={intermediaries}
              serviceGoal={serviceGoal}
              serviceType={serviceType}
            />
            <DeleteIndicator
              lockDelete={indicator.lockDelete}
              className="indicator-delete"
              handleClick={() => handleDelete(sectionKey, indicator, index)}
            />
          </div>
        );
      })}
      {unselectedIndicators.length !== 0 && (
        <StagesModal
          open={open}
          handleOpen={handleOpen}
          handleClose={handleClose}
          handleAdd={onAdd}
          sectionKey={sectionKey}
          indicators={unselectedIndicators}
          header={sectionKey}
          selectedIndicators={selectedIndicators}
        />
      )}
    </StyledDiv>
  );
};

export default SectionRight;
